import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiSummarizer = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-summarizer',
			name: 'AI Summarizer: Get the Key Points of Any Recording',
			description:
				'Summarize videos, podcasts, and lectures with AI: upload a file, get the transcript, and read a short recap in your language. Try Lift today!',
			url: 'https://liftapp.ai/ai-summarizer',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-summarizer#product' },
				{ '@id': 'https://liftapp.ai/ai-summarizer#howto' },
				{ '@id': 'https://liftapp.ai/ai-summarizer#benefits' },
				{ '@id': 'https://liftapp.ai/ai-summarizer#faq' },
				{ '@id': 'https://liftapp.ai/ai-summarizer#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-summarizer#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-summarizer#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-summarizer#product',
			name: 'Audio and Video Summary Generator',
			description:
				'Add a webinar or meeting file, and Lift turns it into a full transcript plus a brief overview of the main ideas. Try Lift today!',
			url: 'https://liftapp.ai/ai-summarizer',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-summarizer#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload a video or audio file',
					text: 'Add a lecture, podcast episode, webinar, or meeting recording straight from your device.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Check the language and audio cues',
					text: 'Keep Auto for detection, or set it yourself to give AI a clear hint.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Create the full transcript',
					text: 'Tap Generate, and Lift turns everything said in the file into text in My Transcriptions.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Get the key points in your language',
					text: 'Open the result, tap Summarize, and pick a different option from the menu to get a translation.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-summarizer#benefits',
			name: 'Who can benefit from the AI Summarizer?',
			description:
				"Lift's AI Summarizer helps students, researchers, busy professionals, non-native speakers, and team leads get the key points of long recordings in minutes.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Students — Summarize recorded lectures and online lessons into key points to review before class.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Researchers and analysts — Scan conference talks and expert interviews to decide which ones deserve a full watch.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Busy professionals — Get the main takeaways from webinars and industry talks without setting aside an hour.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: "Non-native speakers — Read a translated summary of recordings made in a language you don't speak.",
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Team leads — Turn long meeting recordings into a short recap everyone on the project can read in minutes.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl: 'https://liftapp.ai/images/ai-summarizer/benefits.webp',
				name: 'AI Summarizer by Lift',
				description:
					'Laptop on a home desk showing an AI video summary, ready to download as TXT or PDF',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-summarizer#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift AI Summarizer?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'The Lift AI Summarizer turns an uploaded video or audio file into a full transcript and a short summary of its main ideas. It works for lectures, webinars, podcasts, and meetings.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift AI Summarizer work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload a file and tap Generate to get the transcript. Then open it and choose Summarize to see the key points.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I get the summary in another language?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Yes. Lift first writes it in the recording's original language. Pick a different one from the menu and tap Regenerate to translate it.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the summary together with the transcript?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Download lets you export both texts at once or each one separately, as a TXT or PDF file. Copy also works for either tab.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my videos and audio to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your files securely and uses them only to generate your results.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-summarizer#why-lift',
			name: 'Why choose Lift for AI summaries',
			description:
				"Lift's AI summarizer turns meeting recordings into clear sections and next steps, all in one affordable tool on iOS, Android, and Web.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl: 'https://liftapp.ai/images/ai-summarizer/whyLift.webp',
				name: 'Why use Lift AI Summarizer',
				description:
					"Structured meeting recap from Lift's video summarizer, with launch plan highlights ready to copy or download",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-summarizer#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-summarizer#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Summarizer',
					item: 'https://liftapp.ai/ai-summarizer',
				},
			],
		},
	],
};
